/**
 * CR315: Cards View Configuration Section
 * 
 * Controls for grid/list view toggle and customer preferences.
 */

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { LayoutGrid, List } from "lucide-react";
import type { CardsViewConfig } from "@/lib/layout-studio/types";
import { DEFAULT_CARDS_VIEW_CONFIG } from "@/lib/layout-studio/types";

interface CardsViewSectionProps {
  config: CardsViewConfig;
  onChange: (config: CardsViewConfig) => void;
}

export function CardsViewSection({ config, onChange }: CardsViewSectionProps) {
  const mergedConfig = { ...DEFAULT_CARDS_VIEW_CONFIG, ...config };

  const handleDefaultViewChange = (value: "grid" | "list") => {
    onChange({ ...mergedConfig, defaultView: value });
  };

  const handleCustomerToggleChange = (enabled: boolean) => {
    onChange({ ...mergedConfig, customerToggleEnabled: enabled });
  };

  const handleViewEnabledChange = (view: "grid" | "list", enabled: boolean) => {
    const newViewsEnabled = enabled
      ? [...mergedConfig.viewsEnabled.filter(v => v !== view), view]
      : mergedConfig.viewsEnabled.filter(v => v !== view);
    
    // Ensure at least one view is enabled
    if (newViewsEnabled.length === 0) return;
    
    onChange({ ...mergedConfig, viewsEnabled: newViewsEnabled as ("grid" | "list")[] });
  };

  const handlePersistPreferenceChange = (enabled: boolean) => {
    onChange({ ...mergedConfig, persistCustomerPreference: enabled });
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm flex items-center gap-2">
          <LayoutGrid className="h-4 w-4" />
          Cards View
        </CardTitle>
        <CardDescription>
          Configure grid/list view mode and customer preferences.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Default View */}
        <div className="space-y-2">
          <Label className="text-xs">Default View</Label>
          <RadioGroup
            value={mergedConfig.defaultView}
            onValueChange={(v) => handleDefaultViewChange(v as "grid" | "list")}
            className="grid grid-cols-2 gap-2"
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="grid" id="view-grid" />
              <Label htmlFor="view-grid" className="text-xs cursor-pointer flex items-center gap-1">
                <LayoutGrid className="h-3 w-3" />
                Grid
              </Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="list" id="view-list" />
              <Label htmlFor="view-list" className="text-xs cursor-pointer flex items-center gap-1">
                <List className="h-3 w-3" />
                List
              </Label>
            </div>
          </RadioGroup>
        </div>

        {/* Customer Toggle */}
        <div className="flex items-center justify-between">
          <div className="space-y-0.5">
            <Label className="text-xs">Allow customers to choose view</Label>
            <p className="text-[10px] text-muted-foreground">
              Show a grid/list toggle button for customers
            </p>
          </div>
          <Switch
            checked={mergedConfig.customerToggleEnabled}
            onCheckedChange={handleCustomerToggleChange}
          />
        </div>

        {/* Enabled Views (only shown when customer toggle is enabled) */}
        {mergedConfig.customerToggleEnabled && (
          <div className="space-y-2">
            <Label className="text-xs">Enabled Views</Label>
            <div className="flex gap-4">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="enable-grid"
                  checked={mergedConfig.viewsEnabled.includes("grid")}
                  onCheckedChange={(v) => handleViewEnabledChange("grid", !!v)}
                  disabled={mergedConfig.viewsEnabled.length === 1 && mergedConfig.viewsEnabled[0] === "grid"}
                />
                <Label htmlFor="enable-grid" className="text-xs cursor-pointer">
                  Grid
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="enable-list"
                  checked={mergedConfig.viewsEnabled.includes("list")}
                  onCheckedChange={(v) => handleViewEnabledChange("list", !!v)}
                  disabled={mergedConfig.viewsEnabled.length === 1 && mergedConfig.viewsEnabled[0] === "list"}
                />
                <Label htmlFor="enable-list" className="text-xs cursor-pointer">
                  List
                </Label>
              </div>
            </div>
          </div>
        )}

        {/* Persist Customer Preference */}
        {mergedConfig.customerToggleEnabled && (
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label className="text-xs">Remember customer preference</Label>
              <p className="text-[10px] text-muted-foreground">
                Store selection in browser localStorage
              </p>
            </div>
            <Switch
              checked={mergedConfig.persistCustomerPreference}
              onCheckedChange={handlePersistPreferenceChange}
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
}
